import React, { useMemo } from "react";
import { type ChordVoicingResult } from "../../lib/chordEngine";
import { LUTHERIE_PALETTE } from "../../lib/fretboardLutherieTheme";

interface Props {
  voicing: ChordVoicingResult | null;
  chordName: string;
}

const STRING_COUNT = 6;
const MAX_VISIBLE_FRETS = 5;

export const MiniChordNeck: React.FC<Props> = ({ voicing, chordName }) => {
  const { startFret, positions, mutedStrings, barre } = useMemo(() => {
    if (!voicing) {
      return { startFret: 0, positions: [], mutedStrings: [], barre: null };
    }
    const frets = voicing.positions.map((p) => p.fret);
    const pressed = frets.filter((f) => f > 0);
    const minFret = pressed.length > 0 ? Math.min(...pressed) : 0;
    const maxFret = pressed.length > 0 ? Math.max(...pressed) : 0;

    let start = 0;
    if (maxFret <= MAX_VISIBLE_FRETS) {
      start = 0;
    } else {
      start = Math.max(1, minFret);
    }
    return {
      startFret: start,
      positions: voicing.positions,
      mutedStrings: voicing.mutedStrings,
      barre: voicing.barre || null,
    };
  }, [voicing]);

  if (!voicing) {
    return (
      <div className="w-48 h-36 flex items-center justify-center text-xs text-[#B8A99A]">
        —
      </div>
    );
  }

  // ─── Layout: mástil horizontal (igual que GuitarNeck) ───
  // Trastes = líneas verticales, Cuerdas = líneas horizontales
  // String 6 (grave/E) arriba, String 1 (aguda/e) abajo

  const fretW = 42;       // ancho de cada celda de traste
  const stringH = 26;     // alto entre cuerdas
  const nutW = 7;         // ancho cejuela
  const sidePad = 20;     // padding lateral derecho
  const topPad = 18;      // padding superior
  const bottomPad = 14;
  const labelW = 26;      // espacio para etiquetas de cuerda a la izquierda

  // Cuántos trastes mostrar: mínimo necesario + 1 de margen
  const visibleFrets = Math.min(
    MAX_VISIBLE_FRETS,
    Math.max(4, ...positions.map((p) => (p.fret === 0 ? 0 : p.fret - startFret + 1)))
  );

  const boardW = fretW * visibleFrets;
  const boardH = stringH * (STRING_COUNT - 1);

  const svgW = labelW + nutW + boardW + sidePad;
  const svgH = topPad + boardH + bottomPad;

  // Posición X de la cejuela
  const nutX = labelW;
  const hasNut = startFret === 0;

  // Líneas verticales de traste (incluyendo cejuela como línea 0)
  const fretLinesX = Array.from({ length: visibleFrets + 1 }, (_, i) =>
    nutX + nutW + i * fretW
  );

  // Líneas horizontales de cuerda
  // String 6 (grave) arriba → String 1 (aguda) abajo
  const stringLinesY = Array.from({ length: STRING_COUNT }, (_, i) =>
    topPad + i * stringH
  );

  // Centro de cada celda de traste (para colocar dots)
  const cellCentersX = Array.from({ length: visibleFrets }, (_, i) =>
    (fretLinesX[i] + fretLinesX[i + 1]) / 2
  );

  // Procesar posiciones de dedos
  const dotData = positions
    .filter((p) => !mutedStrings.includes(p.string))
    .map((p) => {
      const stringIdx = STRING_COUNT - p.string; // 0=string6(arriba), 5=string1(abajo)
      const isOpen = p.fret === 0;
      // Cuando hay cejuela, el traste 1 va en la celda 0 (entre cejuela y traste1)
      const fretIdx = isOpen ? -1 : p.fret - startFret - (hasNut ? 1 : 0);
      return { stringIdx, fretIdx, isOpen, finger: p.finger };
    })
    .filter((p) => p.isOpen || (p.fretIdx >= 0 && p.fretIdx < visibleFrets));

  // Barre
  const barreData = barre
    ? {
        fromStringIdx: STRING_COUNT - barre.fromString,
        toStringIdx: STRING_COUNT - barre.toString,
        fretIdx: barre.fret - startFret - (hasNut ? 1 : 0),
      }
    : null;

  const stringLabels = ["6E", "5A", "4D", "3G", "2B", "1e"];
  const dotRadius = 10;

  return (
    <div className="flex flex-col items-center">
      <span className="text-base font-bold text-slate-100 mb-2 tracking-tight">{chordName}</span>
      <svg
        width={svgW}
        height={svgH}
        viewBox={`0 0 ${svgW} ${svgH}`}
        className="select-none"
        style={{ overflow: "visible" }}
      >
        <defs>
          <filter id="mcnDotShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.45)" />
          </filter>
          <radialGradient id="mcnDotGlow" cx="35%" cy="30%" r="65%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
          <pattern id="mcnWoodGrain" width="120" height="120" patternUnits="userSpaceOnUse">
            <rect width="120" height="120" fill={LUTHERIE_PALETTE.fingerboard} />
            <path
              d="M0,24 Q40,20 80,26 T120,22"
              stroke={LUTHERIE_PALETTE.fingerboardLight}
              strokeWidth="0.6"
              fill="none"
              opacity="0.12"
            />
          </pattern>
          <linearGradient id="mcnBoardEdge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={LUTHERIE_PALETTE.fingerboardDark} />
            <stop offset="50%" stopColor={LUTHERIE_PALETTE.fingerboard} />
            <stop offset="100%" stopColor={LUTHERIE_PALETTE.fingerboardDark} />
          </linearGradient>
        </defs>

        <rect
          x={nutX - 4}
          y={topPad - 14}
          width={nutW + boardW + 8}
          height={boardH + 28}
          fill="url(#mcnWoodGrain)"
          rx={12}
          stroke="rgba(255,255,255,0.06)"
        />
        <rect
          x={nutX - 4}
          y={topPad - 14}
          width={nutW + boardW + 8}
          height={boardH + 28}
          fill="url(#mcnBoardEdge)"
          rx={12}
          opacity={0.25}
        />

        {hasNut && (
          <rect
            x={nutX}
            y={topPad - 14}
            width={nutW}
            height={boardH + 28}
            rx={3}
            fill={LUTHERIE_PALETTE.nut}
            stroke={LUTHERIE_PALETTE.nutShadow}
            strokeWidth={0.8}
            opacity={0.95}
          />
        )}

        {fretLinesX.map((x, i) => (
          <line
            key={`fret-${i}`}
            x1={x}
            y1={topPad - 12}
            x2={x}
            y2={topPad + boardH + 12}
            stroke={i === 0 && hasNut ? LUTHERIE_PALETTE.fretWireHighlight : LUTHERIE_PALETTE.fretWire}
            strokeWidth={i === 0 && hasNut ? nutW + 1 : 0.9}
            opacity={i === 0 && hasNut ? 0.9 : 0.55}
            strokeLinecap="round"
          />
        ))}

        {stringLinesY.map((y, i) => {
          const wound = i < 3;
          const thickness = 1.2 + i * 0.22;
          return (
            <g key={`str-${i}`}>
              <line
                x1={nutX - 2}
                x2={nutX + nutW + boardW + 2}
                y1={y + 0.8}
                y2={y + 0.8}
                stroke="rgba(0,0,0,0.5)"
                strokeWidth={thickness * 1.2}
                strokeLinecap="round"
                opacity={0.35}
              />
              <line
                x1={nutX - 2}
                x2={nutX + nutW + boardW + 2}
                y1={y}
                y2={y}
                stroke={wound ? "rgba(210,150,95,0.55)" : "rgba(255,255,255,0.18)"}
                strokeWidth={wound ? thickness * 1.5 : thickness}
                strokeLinecap="round"
                opacity={0.9}
              />
            </g>
          );
        })}

        {stringLinesY.map((y, i) => (
          <text
            key={`label-${i}`}
            x={nutX - 10}
            y={y + 4}
            textAnchor="end"
            fontSize={10}
            fontWeight={600}
            fill={mutedStrings.includes(6 - i) ? "rgba(200,210,218,0.35)" : "rgba(200,210,218,0.7)"}
            fontFamily="'Inter', sans-serif"
          >
            {stringLabels[i]}
          </text>
        ))}

        {startFret > 0 && (
          <text
            x={nutX + nutW + fretW / 2}
            y={topPad - 18}
            textAnchor="middle"
            fontSize={13}
            fontWeight={700}
            fill={LUTHERIE_PALETTE.inlayGold}
            fontFamily="'Inter', sans-serif"
          >
            {startFret}
          </text>
        )}

        {/* Barre */}
        {barreData &&
          barreData.fretIdx >= 0 &&
          barreData.fretIdx < visibleFrets && (
            <rect
              x={cellCentersX[barreData.fretIdx] - dotRadius + 1}
              y={
                stringLinesY[Math.min(barreData.fromStringIdx, barreData.toStringIdx)] -
                dotRadius +
                2
              }
              width={(dotRadius - 1) * 2}
              height={
                Math.abs(barreData.toStringIdx - barreData.fromStringIdx) * stringH +
                (dotRadius - 2) * 2
              }
              rx={dotRadius - 1}
              fill={LUTHERIE_PALETTE.noteBorderActive}
              opacity={0.92}
              filter="url(#mcnDotShadow)"
            />
          )}

        {/* Dots */}
        {dotData.map((p, i) => {
          const cy = stringLinesY[p.stringIdx];

          if (p.isOpen) {
            // Cuerda al aire: círculo verde hueco
            return (
              <g key={`dot-${i}`}>
                <circle
                  cx={nutX - 10}
                  cy={cy}
                  r={5.5}
                  fill="none"
                  stroke="#10B981"
                  strokeWidth={2}
                />
              </g>
            );
          }

          const cx = cellCentersX[p.fretIdx];

          return (
            <g key={`dot-${i}`} filter="url(#mcnDotShadow)">
              <circle cx={cx} cy={cy} r={dotRadius} fill={LUTHERIE_PALETTE.noteBorderActive} />
              <circle cx={cx} cy={cy} r={dotRadius} fill="url(#mcnDotGlow)" />
              {p.finger && (
                <text
                  x={cx}
                  y={cy + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={12}
                  fontWeight={700}
                  fill="#FFFFFF"
                  fontFamily="'Inter', sans-serif"
                >
                  {p.finger}
                </text>
              )}
            </g>
          );
        })}

        {/* X para cuerdas muteadas */}
        {mutedStrings.map((s) => {
          const idx = 6 - s; // string 6 → idx 0 (arriba)
          const y = stringLinesY[idx];
          return (
            <g key={`mute-${s}`}>
              <line
                x1={nutX - 15}
                y1={y - 5}
                x2={nutX - 5}
                y2={y + 5}
                stroke="#78716C"
                strokeWidth={1.5}
                strokeLinecap="round"
              />
              <line
                x1={nutX - 15}
                y1={y + 5}
                x2={nutX - 5}
                y2={y - 5}
                stroke="#78716C"
                strokeWidth={1.5}
                strokeLinecap="round"
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};
